import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/effect-cards';

import { useState } from 'react';
import { Autoplay, EffectCards, Pagination } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide, useSwiper } from 'swiper/react';
import styles from './Gallery.module.scss';
import { ArrowButton } from '@/atoms/ArrowButton';
import cn from 'classnames';
import { Variants } from '@/types';
import { GalleryItem } from '../GalleryItem';
import { useAdaptiveWidth } from '@/hooks/useAdaptiveWidth';

function BannerButton({ isNextButton }: { isNextButton?: boolean }) {
  const swiper = useSwiper();

  const handleClick = () => {
    if (isNextButton) {
      swiper.slideNext();
    } else {
      swiper.slidePrev();
    }
  };

  return isNextButton ? (
    <ArrowButton
      variant={Variants.Filled}
      onClick={handleClick}
      className={styles.next}
    />
  ) : (
    <ArrowButton
      variant={Variants.Filled}
      className={styles.prev}
      onClick={handleClick}
    />
  );
}

type GalleryProps = {
  techClosed?: boolean;
  className?: string;
  gallery: string[];
  galleryMob: string[];
  title?: string;
  alert?: {
    name: string;
    icon?: string;
  };
  unaccompanied?: string;
  accompanied?: string;
};

export function Gallery({
  className,
  gallery,
  title,
  galleryMob,
  alert,
  unaccompanied,
  accompanied,
}: GalleryProps) {
  const [isStart, setIsStart] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const { isMobile } = useAdaptiveWidth();

  const sliderClassName = cn(
    styles.slider,
    {
      [styles.noMargin]: gallery && gallery.length < 2,
    },
    className,
  );

  const sliderClassNameMobile = cn(
    styles.sliderMobile,
    {
      [styles.gallery]: gallery,
    },
    className,
  );

  const getIsMobileGalleryNoTabs = () => {
    if (isMobile && galleryMob && galleryMob.length === 0) {
      if (isMobile && gallery && gallery.length < 2) {
        return true;
      }
    }

    if (
      isMobile &&
      galleryMob &&
      galleryMob.length !== 0 &&
      galleryMob.length < 2
    ) {
      return true;
    }

    return false;
  };

  const spaceClassName = cn(styles.space, {
    [styles.lessHeight]: getIsMobileGalleryNoTabs(),
  });

  const handleSlideChange = (swiper: SwiperClass) => {
    setIsStart(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  return (
    <>
      <div className={spaceClassName}></div>
      <Swiper
        className={sliderClassNameMobile}
        pagination={{
          clickable: true,
          bulletClass: styles.bullet,
          bulletActiveClass: styles.bulletActive,
        }}
        modules={[Pagination, Autoplay]}
        spaceBetween={20}
        onSlideChange={handleSlideChange}
        speed={1000}
        autoplay={{
          delay: 5000,
          disableOnInteraction: true,
          pauseOnMouseEnter: true,
        }}
      >
        {galleryMob && galleryMob.length > 0
          ? galleryMob.map((item: string, index: number) => (
              <SwiperSlide key={item + index}>
                <GalleryItem
                  name={index === 0 ? title : undefined}
                  item={item}
                  gallery
                  unaccompanied={index === 0 ? unaccompanied : undefined}
                  accompanied={index === 0 ? accompanied : undefined}
                />
              </SwiperSlide>
            ))
          : gallery && gallery.length > 0
            ? gallery.map((item: string, index: number) => (
                <SwiperSlide key={item + index}>
                  <GalleryItem
                    name={index === 0 ? title : undefined}
                    item={item}
                    gallery
                    alert={index === 0 ? alert : undefined}
                    unaccompanied={index === 0 ? unaccompanied : undefined}
                    accompanied={index === 0 ? accompanied : undefined}
                  />
                </SwiperSlide>
              ))
            : null}
      </Swiper>
      <Swiper
        className={sliderClassName}
        pagination={{
          clickable: true,
          bulletClass: styles.bullet,
          bulletActiveClass: styles.bulletActive,
        }}
        modules={[Pagination, EffectCards, Autoplay]}
        spaceBetween={16}
        onSlideChange={handleSlideChange}
        speed={1000}
        autoplay={{
          delay: 5000,
          disableOnInteraction: true,
          pauseOnMouseEnter: true,
        }}
      >
        {gallery.length > 1 && (
          <div className={styles.buttons}>
            {isStart ? <div /> : <BannerButton />}
            {isEnd ? <div /> : <BannerButton isNextButton />}
          </div>
        )}
        {gallery &&
          gallery.map((item: string, index: number) => (
            <SwiperSlide key={item + index}>
              <GalleryItem
                name={index === 0 ? title : undefined}
                item={item}
                gallery
                alert={index === 0 ? alert : undefined}
                unaccompanied={index === 0 ? unaccompanied : undefined}
                accompanied={index === 0 ? accompanied : undefined}
              />
            </SwiperSlide>
          ))}
      </Swiper>
    </>
  );
}
